import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import Badge from '../../../Badge.jsx';
import { toneFor } from '../constants/statTones';
import { formatValue } from '../shared';

/** Styles whose values are read as quantities, so they right-align. */
const NUMERIC_STYLES = Object.freeze(['NUMBER', 'INTEGER', 'DECIMAL', 'CURRENCY', 'PERCENT']);

/** `data_type` values the contract uses for quantities. */
const NUMERIC_TYPES = Object.freeze(['NUMBER', 'INTEGER', 'DECIMAL', 'NUMERIC', 'BIGINT', 'FLOAT']);

/**
 * True when a column's figures belong on the right edge.
 *
 * Alignment comes from the column's declared type, never from sniffing the
 * first row: a column of counts whose first page happens to be empty would
 * otherwise left-align on page one and jump right on page two.
 */
const isNumeric = (column) =>
  NUMERIC_STYLES.includes(column.format?.style)
  || NUMERIC_TYPES.includes(String(column.data_type ?? '').toUpperCase());

/** `data_type` values the contract uses for a day, and for a point in time. */
const DATE_TYPES = Object.freeze(['DATE']);
const DATETIME_TYPES = Object.freeze(['DATETIME', 'TIMESTAMP', 'TIMESTAMPTZ', 'TIMESTAMP_TZ']);

/**
 * The `format` to print a cell with, filling in a date style from the column's
 * declared type when the contract left `format` empty (CT-5884).
 *
 * `formatValue` has handled `DATE` and `DATETIME` for a while, but only when
 * `format.style` says so — and the backend sends no `format` object at all for
 * these columns, so every timestamp fell through to the string branch and
 * printed as `2026-10-21T14:03:11.000Z`. The type is right there on the
 * column, so use it.
 *
 * An explicit `format.style` always wins; a column with neither is left alone,
 * because guessing a date out of a plain string is how you end up formatting
 * version numbers.
 */
const formatFor = (column) => {
  if (column.format?.style) return column.format;
  const type = String(column.data_type ?? '').toUpperCase();
  if (DATE_TYPES.includes(type)) return { ...column.format, style: 'DATE' };
  if (DATETIME_TYPES.includes(type)) return { ...column.format, style: 'DATETIME' };
  return column.format;
};

/**
 * Detailed, row-by-row review.
 *
 * A deliberately small table, not the platform's `DataTable`: this renders
 * inside a widget slot that can be a third of a dashboard wide, with no
 * sorting, no selection and no pagination. `DataTable` is the right component
 * the moment any of those are wanted — this one exists so a widget-sized
 * table does not drag a full data grid into a 240px card.
 *
 * ## Reading the contract (CT-5641)
 *
 * `TABLE` publishes `columns[{key, label, data_type, format}]` and
 * `rows[{row_key, values}]` — the cells live in `values`, keyed by column, and
 * never on the row itself. Cells arrive raw: a `TIMESTAMP` column is
 * `2026-10-06T14:00:00Z`, not a date a reader can use, so every cell goes
 * through `formatValue` with its own column's `format`.
 *
 * ## `row_key` is the point of the table
 *
 * It is the uuid the row came from. A table in a dashboard is not the
 * destination — it is how someone finds the one record they need and opens
 * it. So the **first column is the link**, when the caller passes
 * `onRowClick`: it is the column that identifies the row, and making the
 * whole row clickable steals the text selection a table is also used for.
 *
 * Without a handler the cell is plain text rather than a link that goes
 * nowhere, which is the kind of thing a reader only discovers by clicking.
 *
 * ## Badges
 *
 * A status column reads at a glance only if it looks different from the
 * identifier beside it. The contract has no flag for that — `data_type` is
 * `TEXT` for both — so the caller names the columns in `badgeColumns`.
 *
 * The header is sticky, because the tall tier scrolls and a table whose
 * column names leave the screen is a grid of unlabelled values.
 *
 * @param {object} props
 * @param {object} props.data `TABLE`'s `columns` + `rows`, or the pre-v2 flat rows.
 * @param {string[]} [props.badgeColumns] Column keys to draw as badges.
 * @param {(row: object) => void} [props.onRowClick] Makes the first cell a link.
 * @param {number} [props.maxRows] Truncate to this many rows. Unset renders all of them.
 * @param {string} [props.className]
 */
function TableWidget({ data, badgeColumns, onRowClick, maxRows, className = '' }) {
  const columns = data?.columns ?? [];

  /*
    One row shape reaches the drawing. The contract nests the cells under
    `values`; a pre-v2 caller put them on the row itself, and that caller is
    still in the stories and in a dashboard or two.
  */
  const rows = useMemo(() => (data?.rows ?? []).map((row, index) => ({
    key: row.row_key ?? row.id ?? String(index),
    values: row.values ?? row,
    raw: row,
  })), [data]);

  if (!columns.length || !rows.length) return null;

  const visible = maxRows ? rows.slice(0, maxRows) : rows;
  const badged = (column) => badgeColumns?.includes(column.key) || column.type === 'badge';

  return (
    <div className={`scrollbar-aimsos-auto min-w-0 overflow-auto ${className}`.trim()}>
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-[1] bg-surface">
          <tr className="border-b border-edge">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`whitespace-nowrap bg-surface px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-content-muted ${
                  isNumeric(column) || column.type === 'number' ? 'text-right' : 'text-left'
                }`}
              >
                {column.label ?? column.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.key} className="border-b border-edge last:border-b-0">
              {columns.map((column, columnIndex) => {
                const value = row.values[column.key];
                const text = formatValue(value, formatFor(column));

                if (badged(column)) {
                  return (
                    <td key={column.key} className="px-2 py-[7px]">
                      <Badge size="small" variant={toneFor(row.values[`${column.key}Tone`]).badge}>
                        {text}
                      </Badge>
                    </td>
                  );
                }

                if (columnIndex === 0 && onRowClick) {
                  return (
                    <td key={column.key} className="px-2 py-[7px]">
                      <button
                        type="button"
                        onClick={() => onRowClick(row.raw)}
                        className="rounded text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {text}
                      </button>
                    </td>
                  );
                }

                return (
                  <td
                    key={column.key}
                    className={`whitespace-nowrap px-2 py-[7px] text-xs text-content ${
                      isNumeric(column) || column.type === 'number' ? 'text-right font-semibold' : ''
                    }`}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

TableWidget.propTypes = {
  /** `TABLE`'s `columns` + `rows`. Pre-v2 rows (cells on the row) still render. */
  data: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
        data_type: PropTypes.string,
        format: PropTypes.object,
        /** Pre-v2 only. */
        type: PropTypes.oneOf(['text', 'number', 'badge']),
      }),
    ).isRequired,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        row_key: PropTypes.string,
        values: PropTypes.object,
      }),
    ).isRequired,
  }).isRequired,
  badgeColumns: PropTypes.arrayOf(PropTypes.string),
  onRowClick: PropTypes.func,
  maxRows: PropTypes.number,
  className: PropTypes.string,
};

export default memo(TableWidget);
